import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert } from "react-bootstrap";
import { FaCrown, FaSearch, FaArrowRight, FaCheck, FaUser, FaChalkboardTeacher, FaCamera, FaUsers } from "react-icons/fa";

const ClassOwnershipModal = ({ show, onHide, schoolid, getTeachers, getClassesBySchoolId, bulkRemoveOrReplaceClassOwner }) => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filter states
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [classOwnershipFilter, setClassOwnershipFilter] = useState("all");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [ownershipAction, setOwnershipAction] = useState("replace"); // "replace" or "remove"

  // Grade levels for filtering
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const ownershipFilters = [
    { value: "all", label: "All Classes" },
    { value: "with_owner", label: "Has Owner" },
    { value: "without_owner", label: "No Owner" },
    { value: "creator_owner", label: "Creator = Owner" },
    { value: "different_owner", label: "Creator ≠ Owner" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [teachersResponse, classesResponse] = await Promise.all([
        getTeachers(schoolid),
        getClassesBySchoolId(schoolid)
      ]);

      console.log("Teachers response:", teachersResponse);
      console.log("Classes response:", classesResponse);

      // Transform teachers data with null checks
      const transformedTeachers = (teachersResponse || []).map(teacher => ({
        id: teacher.id,
        name: teacher.name || 'Unknown',
        email: teacher.email || '',
        active: teacher.active !== false
      })).sort((a, b) => a.name.localeCompare(b.name));

      // Transform classes data with null checks
      const transformedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);
        
        // Find teacher and owner names
        const creator = transformedTeachers.find(t => t.id === classItem.teacherId);
        const owner = transformedTeachers.find(t => t.id === classItem.classOwnerId);
        
        return {
          id: classItem.id,
          name: classItem.name || 'Unnamed Class',
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear || null, // Add academic year
          snapshot: classItem.snapshot || false, // Add snapshot field
          studentNumber: classItem.studentNumber || 0, // Add student count
          teacherId: classItem.teacherId,
          classOwnerId: classItem.classOwnerId,
          creatorName: creator?.name || 'Unknown',
          ownerName: owner?.name || 'No Owner',
          hasOwner: !!classItem.classOwnerId,
          creatorIsOwner: classItem.teacherId === classItem.classOwnerId,
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated
        };
      }).sort((a, b) => {
        // Sort by academic year (newest first), then by name
        if (a.academicYear !== b.academicYear) {
          return b.academicYear?.localeCompare(a.academicYear || '') || 0;
        }
        return a.name.localeCompare(b.name);
      });

      // Extract unique academic years for filter options
      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year) // Remove null/undefined
      )].sort().reverse(); // Sort newest first

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);

      console.log("Transformed teachers:", transformedTeachers);
      console.log("Transformed classes:", transformedClasses);

      setTeachers(transformedTeachers);
      setClasses(transformedClasses);

    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setSelectedClasses([]); // Reset selected classes when changing teacher
  };

  const handleClassSelect = (classItem) => {
    setSelectedClasses(prev => {
      const isSelected = prev.find(c => c.id === classItem.id);
      if (isSelected) {
        return prev.filter(c => c.id !== classItem.id);
      } else {
        return [...prev, classItem];
      }
    });
  };

  const handleSelectAllClasses = () => {
    const filteredClasses = getFilteredClasses();
    if (selectedClasses.length === filteredClasses.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(filteredClasses);
    }
  };

  const getFilteredTeachers = () => {
    return teachers.filter(teacher => {
      const matchesSearch = teacher.name?.toLowerCase().includes(teacherSearchTerm.toLowerCase()) ||
                           teacher.email?.toLowerCase().includes(teacherSearchTerm.toLowerCase());
      const isActive = teacher.active !== false;
      return matchesSearch && isActive;
    });
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           classItem.creatorName?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           classItem.ownerName?.toLowerCase().includes(classSearchTerm.toLowerCase());
      
      let matchesOwnership = true;
      switch (classOwnershipFilter) {
        case "with_owner":
          matchesOwnership = classItem.hasOwner;
          break;
        case "without_owner":
          matchesOwnership = !classItem.hasOwner;
          break;
        case "creator_owner":
          matchesOwnership = classItem.creatorIsOwner;
          break;
        case "different_owner":
          matchesOwnership = classItem.hasOwner && !classItem.creatorIsOwner;
          break;
        default:
          matchesOwnership = true;
      }
      
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch && matchesOwnership;
    });
  };

  const handleTransferOwnershipClick = () => {
    if (selectedClasses.length === 0) {
      setError("Please select at least one class.");
      return;
    }
    
    // Determine action based on whether a teacher is selected
    if (selectedTeacher) {
      setOwnershipAction("replace");
    } else {
      setOwnershipAction("remove");
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    setShowConfirmModal(false);
    await handleTransferOwnership();
  };

  const handleTransferOwnership = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Use your bulk API - pass null for newClassOwnerId if removing ownership
      const newOwnerId = ownershipAction === "replace" ? selectedTeacher.id : null;
      const response = await bulkRemoveOrReplaceClassOwner(
        selectedClasses.map(c => c.id), 
        newOwnerId
      );

      const { successful, failed } = response;

      if (successful && successful.length > 0) {
        const actionText = ownershipAction === "replace" 
          ? `transferred ownership of ${successful.length} class(es) to ${selectedTeacher.name}`
          : `removed ownership from ${successful.length} class(es)`;
        setSuccess(`Successfully ${actionText}`);
      }
      
      if (failed && failed.length > 0) {
        const failureDetails = failed.map(f => `${f.classId}: ${f.error}`).join(', ');
        setError(`Warning: ${failed.length} class(es) could not be updated. Details: ${failureDetails}`);
      }

      if (!successful || successful.length === 0) {
        setError("No classes were updated. Please try again.");
      }

      setSelectedClasses([]);
      setSelectedTeacher(null);
      
      // Reload data to reflect changes
      await loadData();

    } catch (err) {
      setError("Failed to update class ownership. Please try again.");
      console.error("Transfer ownership error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTeacher(null);
    setSelectedClasses([]);
    setError("");
    setSuccess("");
    setTeacherSearchTerm("");
    setClassSearchTerm("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setClassOwnershipFilter("all");
    setOwnershipAction("replace");
    setShowConfirmModal(false);
    onHide();
  };

  const filteredTeachers = getFilteredTeachers();
  const filteredClasses = getFilteredClasses();

  const getOwnershipStatusBadge = (classItem) => {
    if (!classItem.hasOwner) {
      return <Badge bg="warning">No Owner</Badge>;
    }
    if (classItem.creatorIsOwner) {
      return <Badge bg="success">Creator = Owner</Badge>;
    }
    return <Badge bg="info">Separate Owner</Badge>;
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCrown className="me-2" />
          Class Ownership Management
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '700px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Left Panel - Teachers */}
          <Col md={5} className="border-end">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Select New Owner
                <small className="text-muted d-block">Or leave unselected to remove ownership</small>
              </h5>
              {selectedTeacher && (
                <Badge bg="success">
                  Selected: {selectedTeacher.name}
                </Badge>
              )}
            </div>

            {/* Clear Selection Button */}
            <div className="mb-3">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => setSelectedTeacher(null)}
                disabled={!selectedTeacher}
                className="w-100"
              >
                Clear Selection (Remove Ownership)
              </Button>
            </div>

            {/* Teacher Search */}
            <div className="mb-3">
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search teachers..."
                  value={teacherSearchTerm}
                  onChange={(e) => setTeacherSearchTerm(e.target.value)}
                  size="sm"
                />
                <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
              </div>
            </div>

            {/* Teachers List */}
            <div style={{ height: '520px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">Loading teachers...</div>
              ) : filteredTeachers.length === 0 ? (
                <div className="text-center py-4 text-muted">No teachers found</div>
              ) : (
                <ListGroup>
                  {filteredTeachers.map(teacher => (
                    <ListGroup.Item
                      key={teacher.id}
                      className={`d-flex justify-content-between align-items-center cursor-pointer ${
                        selectedTeacher?.id === teacher.id ? 'bg-light border-success' : ''
                      }`}
                      onClick={() => handleTeacherSelect(teacher)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        <div className="fw-medium">{teacher.name}</div>
                        {teacher.email && (
                          <small className="text-muted">{teacher.email}</small>
                        )}
                      </div>
                      {selectedTeacher?.id === teacher.id && (
                        <FaCheck className="text-success" />
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Col>

          {/* Right Panel - Classes */}
          <Col md={7}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Classes 
                <Badge bg="primary" className="ms-2">{selectedClasses.length} selected</Badge>
              </h5>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={handleSelectAllClasses}
                disabled={filteredClasses.length === 0}
              >
                {selectedClasses.length === filteredClasses.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {/* Class Filters */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Select 
                  value={classAcademicYearFilter} 
                  onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {availableAcademicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={classGradeFilter} 
                  onChange={(e) => setClassGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={classSnapshotFilter} 
                  onChange={(e) => setClassSnapshotFilter(e.target.value)}
                  size="sm"
                >
                  {snapshotOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={classOwnershipFilter} 
                  onChange={(e) => setClassOwnershipFilter(e.target.value)}
                  size="sm"
                >
                  {ownershipFilters.map(filter => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search classes, creators, owners..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Classes List */}
            <div style={{ height: '380px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">Loading classes...</div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">No classes found</div>
              ) : (
                <div className="d-grid gap-2">
                  {filteredClasses.map(classItem => (
                    <Card
                      key={classItem.id}
                      className={`cursor-pointer ${
                        selectedClasses.find(c => c.id === classItem.id) ? 'border-primary bg-light' : ''
                      }`}
                      onClick={() => handleClassSelect(classItem)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div className="d-flex align-items-center">
                                <div className="fw-medium me-2">{classItem.name}</div>
                                {classItem.snapshot && (
                                  <Badge bg="secondary" className="me-2">
                                    <FaCamera className="me-1" size={10} />
                                    Snapshot
                                  </Badge>
                                )}
                              </div>
                              <div className="text-end">
                                {getOwnershipStatusBadge(classItem)}
                                {selectedClasses.find(c => c.id === classItem.id) && (
                                  <FaCheck className="text-primary ms-2" />
                                )}
                              </div>
                            </div>
                            
                            <div className="small text-muted">
                              <div className="d-flex justify-content-between mb-1">
                                <span>
                                  {classItem.grade}
                                  {classItem.academicYear && ` • ${classItem.academicYear}`}
                                  {!classItem.snapshot && classItem.studentNumber > 0 && (
                                    <Badge bg="light" text="dark" className="ms-2">
                                      <FaUsers className="me-1" size={10} />
                                      {classItem.studentNumber} student{classItem.studentNumber !== 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                </span>
                                {classItem.trial_class && (
                                  <Badge bg="warning" className="ms-1">Fake</Badge>
                                )}
                              </div>
                              
                              <div className="d-flex align-items-center mb-1">
                                <FaChalkboardTeacher className="me-1" />
                                <span className="me-2">Creator:</span>
                                <span className="fw-medium">{classItem.creatorName}</span>
                              </div>
                              
                              <div className="d-flex align-items-center">
                                <FaCrown className="me-1" />
                                <span className="me-2">Owner:</span>
                                <span className={`fw-medium ${classItem.hasOwner ? 'text-dark' : 'text-muted'}`}>
                                  {classItem.ownerName}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedClasses.length > 0 && (
              <span>
                <FaArrowRight className="me-1" />
                {selectedTeacher 
                  ? `Transfer ownership of ${selectedClasses.length} class(es) to ${selectedTeacher.name}`
                  : `Remove ownership from ${selectedClasses.length} class(es)`
                }
                {selectedClasses.some(c => c.snapshot) && (
                  <Badge bg="warning" className="ms-2 text-dark">
                    <FaCamera className="me-1" size={10} />
                    Includes Snapshots
                  </Badge>
                )}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button 
              variant={selectedTeacher ? "primary" : "warning"}
              onClick={handleTransferOwnershipClick}
              disabled={selectedClasses.length === 0 || loading}
            >
              {loading 
                ? "Processing..." 
                : selectedTeacher 
                  ? "Transfer Ownership" 
                  : "Remove Ownership"
              }
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center">
            <FaCrown className="me-2" />
            {ownershipAction === "replace" ? "Confirm Ownership Transfer" : "Confirm Ownership Removal"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                 style={{ width: '60px', height: '60px' }}>
              <FaArrowRight className="text-primary" size={24} />
            </div>
            <h5 className="mb-2">
              {ownershipAction === "replace" 
                ? `Transfer Ownership of ${selectedClasses.length} Class${selectedClasses.length !== 1 ? 'es' : ''}`
                : `Remove Ownership from ${selectedClasses.length} Class${selectedClasses.length !== 1 ? 'es' : ''}`
              }
            </h5>
            <p className="text-muted mb-0">Please review the details below before confirming</p>
          </div>

          {/* Classes List */}
          <div className="mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <Badge bg="primary" className="me-2">{selectedClasses.length}</Badge>
              Selected Classes
            </h6>
            <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {selectedClasses.map((classItem, index) => (
                <div key={classItem.id} className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 bg-white rounded border-start border-primary border-3" 
                     style={{ borderStartWidth: '4px !important' }}>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <div className="fw-medium text-dark me-2">{classItem.name}</div>
                      {classItem.snapshot && (
                        <Badge bg="secondary">
                          <FaCamera className="me-1" size={10} />
                          Snapshot
                        </Badge>
                      )}
                    </div>
                    <small className="text-muted">
                      Current Owner: {classItem.ownerName} • Grade: {classItem.grade}
                      {classItem.academicYear && ` • ${classItem.academicYear}`}
                      {!classItem.snapshot && classItem.studentNumber > 0 && 
                        ` • ${classItem.studentNumber} students`}
                    </small>
                  </div>
                  {getOwnershipStatusBadge(classItem)}
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Details */}
          <div className="rounded-3 p-4 mb-4" 
               style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="row align-items-center">
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">CURRENT OWNERS</div>
                  <div className="fw-medium">Multiple Owners</div>
                  <small className="text-muted">Various teachers</small>
                </div>
              </div>
              <div className="col-2 text-center">
                <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px' }}>
                  <FaArrowRight className="text-white" size={16} />
                </div>
              </div>
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">
                    {ownershipAction === "replace" ? "NEW OWNER" : "AFTER REMOVAL"}
                  </div>
                  <div className={`fw-bold ${ownershipAction === "replace" ? "text-success" : "text-warning"}`}>
                    {ownershipAction === "replace" ? selectedTeacher?.name : "No Owner"}
                  </div>
                  <small className="text-muted">
                    {ownershipAction === "replace" ? selectedTeacher?.email : "Ownership will be removed"}
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {selectedClasses.some(c => c.snapshot) && (
            <Alert variant="warning" className="mb-3">
              <FaCamera className="me-2" />
              <strong>Snapshot Classes:</strong> Some selected classes are snapshots. 
              Changing ownership of historical records may affect data integrity.
            </Alert>
          )}

          <div className="alert alert-warning d-flex align-items-start" role="alert">
            <div className="me-2">⚠️</div>
            <div>
              <strong>Important:</strong> This action will {ownershipAction === "replace" ? "change the ownership of" : "remove ownership from"} all selected classes. 
              {ownershipAction === "replace" 
                ? " The new owner will have full control over these classes." 
                : " Classes without owners may have limited functionality."
              } This change cannot be undone easily.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-4">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                <strong>{selectedClasses.length}</strong> class{selectedClasses.length !== 1 ? 'es' : ''} will be {ownershipAction === "replace" ? "transferred" : "updated"}
              </small>
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="me-3">
                Cancel
              </Button>
              <Button 
                variant={ownershipAction === "replace" ? "primary" : "warning"} 
                onClick={handleConfirmTransfer} 
                size="lg" 
                className="px-4"
              >
                {ownershipAction === "replace" ? "Confirm Transfer" : "Confirm Removal"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default ClassOwnershipModal;